/**
 * GameLobby Component
 * 
 * UI component for displaying multiplayer lobby with players, share code, and game controls.
 * Shows session state, player list, and provides actions for game creators and participants.
 */

import { GameSession } from '../../multiplayer/services/GameSession';
import './GameLobby.css';

// Inline types to avoid module resolution issues
interface GameSettings {
  sessionId: string;
  spectatorCanSeeLetters: boolean;
  handicaps: Map<number, any>;
  turnTimeLimit: number;
  allowMidGameJoining: boolean;
}

interface SessionState {
  sessionId: string | null;
  shareCode: string | null;
  isMultiplayer: boolean;
  phase: SessionPhase;
  role: PlayerRole | null;
  playerNumber: number | null;
  players: SessionPlayer[];
  settings: GameSettings | null;
  maxPlayers: number;
  isCreator: boolean;
  gameState: any; // Will be typed properly when integrated with game
}

interface SessionPlayer {
  playerId: string;
  sessionId: string;
  displayName: string;
  playerNumber: number | null;
  role: PlayerRole;
  connectionStatus: ConnectionStatus;
  joinedAt: Date;
  lastActivityAt: Date;
}

const PlayerRole = { PLAYER: 'PLAYER', SPECTATOR: 'SPECTATOR' } as const;
type PlayerRole = typeof PlayerRole[keyof typeof PlayerRole];

const ConnectionStatus = { CONNECTED: 'CONNECTED', DISCONNECTED: 'DISCONNECTED', RECONNECTING: 'RECONNECTING' } as const;
type ConnectionStatus = typeof ConnectionStatus[keyof typeof ConnectionStatus];

const SessionPhase = { WAITING: 'WAITING', PLAYING: 'PLAYING', FINISHED: 'FINISHED' } as const;
type SessionPhase = typeof SessionPhase[keyof typeof SessionPhase];

export interface GameLobbyOptions {
  onStartGame?: () => void;
  onLeaveSession?: () => void;
  onInvitePlayer?: (shareCode: string) => void;
  onPlayerAction?: (playerId: string, action: 'kick' | 'promote') => void;
}

export class GameLobby {
  private element!: HTMLElement;
  private gameSession: GameSession;
  private options: GameLobbyOptions;
  private isVisible: boolean = false;
  private updateTimer: NodeJS.Timeout | null = null;

  constructor(gameSession: GameSession, options: GameLobbyOptions = {}) {
    this.gameSession = gameSession;
    this.options = options;
    this.createElement();
    this.setupEventHandlers();
    this.setupGameSessionCallbacks();
    this.startPeriodicUpdates();
  }

  // ==================== Element Creation ====================

  private createElement(): void {
    this.element = document.createElement('div');
    this.element.className = 'game-lobby-overlay';
    this.element.innerHTML = `
      <div class="game-lobby-modal">
        <!-- Header Section -->
        <div class="game-lobby-header">
          <div class="lobby-title">
            <h2>Game Lobby</h2>
            <div class="lobby-status">Waiting for players...</div>
          </div>
          <button class="leave-lobby-button" type="button">Leave Game</button>
        </div>
        
        <!-- Share Code Section -->
        <div class="share-code-section">
          <div class="share-code-display">
            <label>Share Code:</label>
            <div class="share-code-value">----</div>
            <button class="copy-code-button" type="button" title="Copy share code">
              <span class="copy-icon">📋</span>
              <span class="copy-text">Copy</span>
            </button>
          </div>
          <div class="share-code-instructions">
            Share this code with friends to let them join your game
          </div>
        </div>
        
        <!-- Players Section -->
        <div class="players-section">
          <div class="players-header">
            <h3>Players <span class="player-count">0/4</span></h3>
            <button class="invite-button" type="button">Invite Friend</button>
          </div>
          
          <div class="players-list">
            <!-- Player items will be dynamically created here -->
          </div>
          
          <div class="spectators-list" style="display: none;">
            <h4>Spectators <span class="spectator-count">0</span></h4>
            <div class="spectator-items">
              <!-- Spectator items will be dynamically created here -->
            </div>
          </div>
        </div>
        
        <!-- Game Settings Section -->
        <div class="game-settings-section" style="display: none;">
          <h4>Game Settings</h4>
          <div class="settings-grid">
            <div class="setting-item">
              <label>Max Players:</label>
              <span class="setting-value">4</span>
            </div>
            <div class="setting-item">
              <label>Turn Time:</label>
              <span class="setting-value">2 minutes</span>
            </div>
            <div class="setting-item">
              <label>Bonus Letters:</label>
              <span class="setting-value">Enabled</span>
            </div>
          </div>
        </div>
        
        <!-- Action Section -->
        <div class="lobby-actions">
          <div class="connection-status">
            <div class="status-indicator connected"></div>
            <span class="connection-text">Connected</span>
          </div>
          
          <div class="action-buttons">
            <button class="start-game-button" type="button" disabled>Start Game</button>
            <button class="settings-button" type="button" style="display: none;">Settings</button>
          </div>
        </div>
        
        <!-- Loading/Status Overlay -->
        <div class="lobby-loading" style="display: none;">
          <div class="loading-content">
            <div class="loading-spinner"></div>
            <span class="loading-text">Starting game...</span>
          </div>
        </div>
      </div>
    `;

    // Add to document
    document.body.appendChild(this.element);
  }

  // ==================== Event Handlers ====================

  private setupEventHandlers(): void {
    // Leave lobby button
    const leaveButton = this.element.querySelector('.leave-lobby-button') as HTMLButtonElement;
    leaveButton.addEventListener('click', () => {
      this.handleLeaveSession();
    });

    // Copy share code button
    const copyButton = this.element.querySelector('.copy-code-button') as HTMLButtonElement;
    copyButton.addEventListener('click', () => {
      this.handleCopyShareCode();
    });

    // Start game button
    const startButton = this.element.querySelector('.start-game-button') as HTMLButtonElement;
    startButton.addEventListener('click', () => {
      this.handleStartGame();
    });

    // Invite button
    const inviteButton = this.element.querySelector('.invite-button') as HTMLButtonElement;
    inviteButton.addEventListener('click', () => {
      this.handleInvitePlayer();
    });

    // Settings button
    const settingsButton = this.element.querySelector('.settings-button') as HTMLButtonElement;
    settingsButton.addEventListener('click', () => {
      this.handleShowSettings();
    });

    // Overlay click handling (prevent closing on modal click)
    this.element.addEventListener('click', (e) => {
      if (e.target === this.element) {
        // Could implement click-outside-to-minimize functionality
      }
    });

    // Escape key handling
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isVisible) {
        this.handleLeaveSession();
      }
    });
  }

  // ==================== Game Session Integration ====================

  private setupGameSessionCallbacks(): void {
    this.gameSession.setCallbacks({
      onPlayerJoined: (data) => {
        this.updateDisplay();
        this.showPlayerJoinedNotification(data.player.displayName);
      },

      onPlayerLeft: (data) => {
        this.updateDisplay();
        this.showPlayerLeftNotification(data.displayName, data.reason);
      },

      onSessionEnded: () => {
        this.hide();
      },

      onConnectionLost: () => {
        this.updateConnectionStatus('disconnected');
      },

      onConnectionRestored: () => {
        this.updateConnectionStatus('connected');
      }
    });
  }

  // ==================== Display Updates ====================

  private updateDisplay(): void {
    const sessionState = this.gameSession.getSessionState();

    if (!sessionState.isMultiplayer) {
      this.hide();
      return;
    }

    this.updateHeaderInfo(sessionState);
    this.updateShareCodeDisplay(sessionState);
    this.updatePlayersList(sessionState);
    this.updateGameSettings(sessionState);
    this.updateActionButtons(sessionState);
  }

  private updateHeaderInfo(sessionState: SessionState): void {
    const statusElement = this.element.querySelector('.lobby-status') as HTMLElement;
    const playerCount = sessionState.players.filter(p => p.role === PlayerRole.PLAYER).length;
    const maxPlayers = sessionState.maxPlayers;
    
    if (playerCount >= 2) {
      if (playerCount >= maxPlayers) {
        statusElement.textContent = 'Ready to start! (Full lobby)';
      } else {
        statusElement.textContent = 'Ready to start!';
      }
      statusElement.className = 'lobby-status ready';
    } else {
      statusElement.textContent = 'Waiting for players to join...';
      statusElement.className = 'lobby-status waiting';
    }
  }

  private updateShareCodeDisplay(sessionState: SessionState): void {
    const shareCodeValue = this.element.querySelector('.share-code-value') as HTMLElement;
    const copyButton = this.element.querySelector('.copy-code-button') as HTMLButtonElement;
    
    shareCodeValue.textContent = sessionState.shareCode || '----';
    copyButton.disabled = !sessionState.shareCode;
  }

  private updatePlayersList(sessionState: SessionState): void {
    const playersList = this.element.querySelector('.players-list') as HTMLElement;
    const playersCountElement = this.element.querySelector('.player-count') as HTMLElement;
    const spectatorsSection = this.element.querySelector('.spectators-list') as HTMLElement;
    const spectatorsCountElement = this.element.querySelector('.spectator-count') as HTMLElement;
    const spectatorsContainer = this.element.querySelector('.spectator-items') as HTMLElement;

    // Update player count
    const playerCount = sessionState.players.filter(p => p.role === PlayerRole.PLAYER).length;
    const spectatorCount = sessionState.players.filter(p => p.role === PlayerRole.SPECTATOR).length;
    playersCountElement.textContent = `${playerCount}/${sessionState.maxPlayers}`;

    // Clear existing players
    playersList.innerHTML = '';

    // Add players
    const players = sessionState.players
      .filter(p => p.role === PlayerRole.PLAYER)
      .sort((a, b) => (a.playerNumber || 0) - (b.playerNumber || 0));

    players.forEach(player => {
      const playerElement = this.createPlayerElement(player, sessionState);
      playersList.appendChild(playerElement);
    });

    // Handle spectators
    if (spectatorCount > 0) {
      spectatorsSection.style.display = 'block';
      spectatorsCountElement.textContent = spectatorCount.toString();
      
      spectatorsContainer.innerHTML = '';
      const spectators = sessionState.players.filter(p => p.role === PlayerRole.SPECTATOR);
      spectators.forEach(spectator => {
        const spectatorElement = this.createSpectatorElement(spectator, sessionState);
        spectatorsContainer.appendChild(spectatorElement);
      });
    } else {
      spectatorsSection.style.display = 'none';
    }
  }

  private createPlayerElement(player: SessionPlayer, sessionState: SessionState): HTMLElement {
    const playerDiv = document.createElement('div');
    playerDiv.className = `player-item ${player.connectionStatus.toLowerCase()}`;
    
    const isCurrentPlayer = player.playerId === this.gameSession.getCurrentPlayer()?.playerId;
    const canManage = sessionState.isCreator && !isCurrentPlayer;
    
    playerDiv.innerHTML = `
      <div class="player-avatar">
        <div class="player-number">${player.playerNumber || '?'}</div>
      </div>
      <div class="player-info">
        <div class="player-name">
          ${player.displayName}${isCurrentPlayer ? ' (You)' : ''}
          ${sessionState.isCreator && player.playerId === sessionState.players[0]?.playerId ? ' 👑' : ''}
        </div>
        <div class="player-status">
          ${this.getPlayerStatusText(player)}
        </div>
      </div>
      <div class="player-actions">
        ${canManage ? `
          <button class="player-action-button kick-button" data-player-id="${player.playerId}" title="Remove player">
            ×
          </button>
        ` : ''}
      </div>
    `;

    // Add event listeners for player actions
    if (canManage) {
      const kickButton = playerDiv.querySelector('.kick-button') as HTMLButtonElement;
      kickButton?.addEventListener('click', () => {
        this.handlePlayerAction(player.playerId, 'kick');
      });
    }

    return playerDiv;
  }

  private createSpectatorElement(spectator: SessionPlayer, _sessionState: SessionState): HTMLElement {
    const spectatorDiv = document.createElement('div');
    spectatorDiv.className = `spectator-item ${spectator.connectionStatus.toLowerCase()}`;
    
    const isCurrentPlayer = spectator.playerId === this.gameSession.getCurrentPlayer()?.playerId;
    
    spectatorDiv.innerHTML = `
      <div class="spectator-info">
        <span class="spectator-name">${spectator.displayName}${isCurrentPlayer ? ' (You)' : ''}</span>
        <span class="spectator-status">${this.getPlayerStatusText(spectator)}</span>
      </div>
    `;

    return spectatorDiv;
  }

  private getPlayerStatusText(player: SessionPlayer): string {
    switch (player.connectionStatus) {
      case ConnectionStatus.CONNECTED:
        return 'Online';
      case ConnectionStatus.DISCONNECTED:
        return 'Disconnected';
      case ConnectionStatus.RECONNECTING:
        return 'Reconnecting...';
      default:
        return 'Unknown';
    }
  }

  private updateGameSettings(sessionState: SessionState): void {
    const settingsSection = this.element.querySelector('.game-settings-section') as HTMLElement;
    
    if (sessionState.settings && sessionState.isCreator) {
      settingsSection.style.display = 'block';
      
      // Update settings values if we have them
      // This would need to be expanded based on actual game settings structure
    } else {
      settingsSection.style.display = 'none';
    }
  }

  private updateActionButtons(sessionState: SessionState): void {
    const startButton = this.element.querySelector('.start-game-button') as HTMLButtonElement;
    const settingsButton = this.element.querySelector('.settings-button') as HTMLButtonElement;
    
    // Start button logic
    if (sessionState.isCreator) {
      const playerCount = sessionState.players.filter(p => p.role === PlayerRole.PLAYER).length;
      const connectedCount = sessionState.players.filter(p => 
        p.role === PlayerRole.PLAYER && p.connectionStatus === ConnectionStatus.CONNECTED
      ).length;
      
      startButton.disabled = playerCount < 2 || connectedCount !== playerCount;
      startButton.textContent = playerCount < 2 ? 'Need 2+ Players' : 'Start Game';
      startButton.style.display = 'block';
    } else {
      startButton.style.display = 'none';
    }
    
    // Settings button (for creators)
    settingsButton.style.display = sessionState.isCreator ? 'block' : 'none';
  }

  private updateConnectionStatus(status: 'connected' | 'disconnected' | 'reconnecting'): void {
    const statusIndicator = this.element.querySelector('.status-indicator') as HTMLElement;
    const connectionText = this.element.querySelector('.connection-text') as HTMLElement;
    
    statusIndicator.className = `status-indicator ${status}`;
    
    switch (status) {
      case 'connected':
        connectionText.textContent = 'Connected';
        break;
      case 'disconnected':
        connectionText.textContent = 'Disconnected';
        break;
      case 'reconnecting':
        connectionText.textContent = 'Reconnecting...';
        break;
    }
  }

  // ==================== Event Handlers ====================

  private handleLeaveSession(): void {
    if (this.options.onLeaveSession) {
      this.options.onLeaveSession();
    } else {
      this.gameSession.leaveSession();
      this.hide();
    }
  }

  private handleCopyShareCode(): void {
    const shareCode = this.gameSession.getShareCode();
    if (shareCode) {
      navigator.clipboard.writeText(shareCode).then(() => {
        this.showTemporaryMessage('Share code copied!');
      }).catch((err) => {
        console.error('Failed to copy share code:', err);
        this.showTemporaryMessage('Failed to copy code');
      });
    }
  }

  private handleStartGame(): void {
    if (this.options.onStartGame) {
      this.options.onStartGame();
    } else {
      // Default start game logic
      console.log('[GameLobby] Starting game...');
      this.setLoadingState(true, 'Starting game...');
    }
  }

  private handleInvitePlayer(): void {
    const shareCode = this.gameSession.getShareCode();
    if (shareCode && this.options.onInvitePlayer) {
      this.options.onInvitePlayer(shareCode);
    }
  }

  private handleShowSettings(): void {
    // Toggle settings section visibility
    const settingsSection = this.element.querySelector('.game-settings-section') as HTMLElement;
    const isVisible = settingsSection.style.display !== 'none';
    settingsSection.style.display = isVisible ? 'none' : 'block';
  }

  private handlePlayerAction(playerId: string, action: 'kick' | 'promote'): void {
    if (this.options.onPlayerAction) {
      this.options.onPlayerAction(playerId, action);
    }
  }

  // ==================== Utility Methods ====================

  private showPlayerJoinedNotification(playerName: string): void {
    this.showTemporaryMessage(`${playerName} joined the game`);
  }

  private showPlayerLeftNotification(playerName: string, reason: string): void {
    const reasonText = reason === 'DISCONNECTED' ? 'disconnected' : 'left the game';
    this.showTemporaryMessage(`${playerName} ${reasonText}`);
  }

  private showTemporaryMessage(message: string, duration: number = 3000): void {
    // Create or update temporary message display
    let messageEl = this.element.querySelector('.temporary-message') as HTMLElement;
    if (!messageEl) {
      messageEl = document.createElement('div');
      messageEl.className = 'temporary-message';
      this.element.querySelector('.game-lobby-modal')?.appendChild(messageEl);
    }
    
    messageEl.textContent = message;
    messageEl.style.display = 'block';
    
    setTimeout(() => {
      messageEl.style.display = 'none';
    }, duration);
  }

  private setLoadingState(loading: boolean, message: string = 'Loading...'): void {
    const loadingOverlay = this.element.querySelector('.lobby-loading') as HTMLElement;
    const loadingText = this.element.querySelector('.loading-text') as HTMLElement;
    
    loadingText.textContent = message;
    loadingOverlay.style.display = loading ? 'flex' : 'none';
  }

  // ==================== Periodic Updates ====================

  private startPeriodicUpdates(): void {
    this.updateTimer = setInterval(() => {
      if (this.isVisible && this.gameSession.isInSession()) {
        this.updateDisplay();
      }
    }, 2000); // Update every 2 seconds
  }

  // ==================== Public Interface ====================

  public show(): void {
    this.isVisible = true;
    this.element.style.display = 'flex';
    this.updateDisplay();
  }

  public hide(): void {
    this.isVisible = false;
    this.element.style.display = 'none';
  }

  public isShown(): boolean {
    return this.isVisible;
  }

  public refresh(): void {
    this.updateDisplay();
  }

  public setOptions(options: GameLobbyOptions): void {
    this.options = { ...this.options, ...options };
  }

  // ==================== Cleanup ====================

  public destroy(): void {
    if (this.updateTimer) {
      clearInterval(this.updateTimer);
      this.updateTimer = null;
    }
    
    this.element.remove();
  }
}